import { Link } from 'react-router-dom';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  MapPin, 
  ArrowRight,
  Video,
  Users,
  Rocket,
  Bell,
  PartyPopper
} from 'lucide-react';
import { getUpcomingEvents } from '@/config/events';

const typeConfig = {
  community: {
    label: 'Community Event',
    icon: Users,
    color: 'bg-slate-100 text-slate-700',
  },
  conference: {
    label: 'Conference',
    icon: Users,
    color: 'bg-blue-100 text-blue-700',
  },
  launch: {
    label: 'Product Launch',
    icon: Rocket,
    color: 'bg-green-100 text-green-700',
  },
  deadline: {
    label: 'Deadline',
    icon: Bell,
    color: 'bg-red-100 text-red-700',
  },
  webinar: {
    label: 'Webinar',
    icon: Video,
    color: 'bg-blue-100 text-blue-700',
  },
  celebration: {
    label: 'Celebration',
    icon: PartyPopper,
    color: 'bg-blue-100 text-blue-700',
  },
};

const EventCard = ({ event }) => {
  const config = typeConfig[event.type] || typeConfig.community;
  const Icon = config.icon;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div
      data-testid={`event-${event.id}`}
      className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-lg transition-shadow"
    >
      <div className="flex items-start justify-between mb-3">
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded ${config.color}`}>
          <Icon className="w-3 h-3" />
          {config.label}
        </span>
      </div>
      
      <h3 className="font-semibold text-slate-900 mb-2">
        {event.title}
      </h3>
      
      <p className="text-slate-600 text-sm leading-relaxed mb-4">
        {event.description}
      </p>
      
      <div className="space-y-1.5 pt-3 border-t border-slate-100">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <CalendarIcon className="w-4 h-4 text-blue-600" />
          <span>{formatDate(event.date)}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Clock className="w-4 h-4 text-blue-600" />
          <span>{event.time}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <MapPin className="w-4 h-4 text-blue-600" />
          <span>{event.location}</span>
        </div>
      </div>
    </div>
  );
};

export default function CalendarPage() {
  const upcomingEvents = getUpcomingEvents();

  return (
    <div>
      {/* Hero Section */}
      <section 
        data-testid="calendar-hero"
        className="relative pt-28 pb-16 bg-slate-50"
      >
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-blue-600 font-medium text-sm tracking-wide uppercase mb-3">
              Events & Calendar
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-5">
              Foundation Calendar
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Stay up to date with our upcoming events, webinars, community 
              gatherings, and important deadlines.
            </p>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section data-testid="events-list" className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-3">
              Upcoming Events
            </h2>
            <p className="text-slate-600">
              Mark your calendar for these upcoming foundation activities.
            </p>
          </div>

          {upcomingEvents.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <CalendarIcon className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                No Upcoming Events
              </h3>
              <p className="text-slate-600 max-w-md mx-auto">
                Check back soon for new events, or subscribe to our newsletter 
                to be notified when new events are announced.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Event Types Legend */}
      <section data-testid="event-types" className="py-12 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="text-xl font-semibold text-slate-900 mb-6 text-center">
            Event Types
          </h2>
          
          <div className="flex flex-wrap justify-center gap-3">
            {Object.entries(typeConfig).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <div
                  key={key}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded ${config.color}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{config.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="calendar-cta" className="py-16 bg-slate-800">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Want to Host an Event With Us?
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-8">
            Partner with the Hagen-Family-Foundation to host educational workshops, 
            community events, or awareness campaigns.
          </p>
          <Link
            to="/contact"
            data-testid="calendar-contact-cta"
            className="inline-flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md px-6 py-3 font-medium transition-colors"
          >
            Get In Touch
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
